import {APIRegistry} from '@spothero/utils/api';
import SentryUtils from '@spothero/utils/sentry';
import StorageUtils from '@spothero/utils/storage';
import isNil from 'lodash/isNil';
import Config from 'config';
import BranchUtils from 'utils/branch';
import CMSUtils from 'utils/cms';
import ExperimentUtils from 'utils/experiment';
import GTMUtils from 'utils/gtm';
import loadThirdPartyScripts from 'utils/third-party';
import SegmentUtils from 'utils/segment';
import UserUtils from 'utils/user-utils';

const startup = async ({
    configData,
    isBrowser,
    pageType,
    destination,
    user,
    serverCookies = null,
    optimizelyData = null,
}) => {
    Config.set(configData);

    // TODO: delete this if-statement (and its contents) no later than November 2021.
    if (isBrowser && window.location.hostname === 'awsstaging.kickthe.tires') {
        // When on staging, clear any "csrftoken" cookie attached to the full staging domain.
        // Explanation: in November 2021 we changed the cookie domain for the "csrftoken" to
        // ".kickthe.tires". This led to sporadic bugs in cases where users with long lived
        // sessions maintained an expired CSRF token on the old domain. To address this, we
        // delete any "csrftoken" cookie on the old domain.
        document.cookie =
            'csrftoken=; Path=/; Domain=awsstaging.kickthe.tires; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    }

    if (!APIRegistry.get('distance')) {
        APIRegistry.create({
            name: 'distance',
            config: {
                baseUrl: Config.distanceAPIUrl,
            },
        });
    }

    /* V2-CLEANUP
    Remove this ratesSearch APIRegistry, and confirm that nothing else is relying on it.
    This is used on all Search, Spot Details, and Checkout for all verticals.
    */
    if (!APIRegistry.get('ratesSearch')) {
        const searchAPIUtils = APIRegistry.create({
            name: 'ratesSearch',
            config: {
                baseUrl: Config.spotHeroAPIBasePath,
                additionalHeaders: {
                    'x-spothero-spa': 'consumer-web', // Used by DevTools to track web application traffic
                    'SpotHero-Version': '2019-08-13',
                },
            },
        });

        searchAPIUtils.addRetry({
            retries: 10,
        });
    }

    if (!APIRegistry.get('craig')) {
        APIRegistry.create({
            name: 'craig',
            config: {
                baseUrl: Config.craigUrl,
            },
        });
    }

    if (!APIRegistry.get('craigSearch')) {
        const craigAPIUtils = APIRegistry.create({
            name: 'craigSearch',
            config: {
                baseUrl: Config.craigUrl,
            },
        });

        craigAPIUtils.addRetry({
            retries: 10,
        });
    }

    if (!APIRegistry.get('auth')) {
        APIRegistry.create({
            name: 'auth',
            config: {
                baseUrl: Config.siteUrl,
            },
        });
    }

    if (isBrowser) {
        if (Config.isDeployed) {
            SentryUtils.init({
                apiKey: Config.sentryApiPublicKey,
                isAuthenticated: false,
                release: Config.release,
                environment: Config.isProduction ? 'production' : 'staging',
                additionalSentryOptions: {
                    whitelistUrls: ['spothero.com', 'kickthe.tires'],
                },
            });
        }

        if (Config.branchIOApiKey) {
            BranchUtils.init(Config.branchIOApiKey);
        }

        // remove the time to first result session stored so it can be re-created on search
        StorageUtils.remove('sh-ttfr-tracked', 'session');

        CMSUtils.init();

        /* eslint-disable no-undefined */
        GTMUtils.push({
            client: Config.isMobile ? 'mobile' : 'desktop',
            dateInt: `${new Date().getTime()}`,
            facebookLogoutUrl: '/logout',
            userId: undefined, // eslint-disable-line no-undefined
            facebookOAuth: undefined, // eslint-disable-line no-undefined
            facebookAppId: Config.facebookAppId,
            facebookChannelUrl: Config.facebookChannelUrl,
        });
        /* eslint-enable no-undefined */

        loadThirdPartyScripts();
    }

    const optimizelyDatafile = await ExperimentUtils.init({
        isDev: Config.isDev,
        serverCookies,
        optimizelyData,
        user,
        onLoad: () => {
            // https://segment.com/docs/destinations/optimizely-web/#optimizely-full-stack-javascript
            // See section titled Optimizely Full Stack (JavaScript) - Getting Started
            // Ensure Segment loads only after Optimizely has been initialized
            if (isBrowser) {
                if (Config.isSegmentEnabled) {
                    SegmentUtils.init({
                        apiKey: Config.isDeployed
                            ? Config.segmentApiKey
                            : 'debug',
                        debug: !Config.isDeployed,
                    });
                }

                // After GTM, Sentry (browser), and Segment have been initialized,
                // call their respective identify functions.
                // This is wrapped in a check, as calling identify with no user is equivalent to logging out.
                if (!isNil(user)) {
                    UserUtils.identify(user);
                }

                // Optimizely's setForcedVariation is only persistant per client, but we want it to be persistant between page loads
                // so store all forced experiments in local storage with {experimentName => variant} pairings and iterate through them
                // on browser page load
                const forcedExperiments = StorageUtils.get(
                    'sh-forcedExperiments'
                );

                if (forcedExperiments) {
                    Object.entries(forcedExperiments).forEach(
                        ([experimentName, variant]) => {
                            ExperimentUtils.setForcedVariation(
                                experimentName,
                                variant
                            );
                        }
                    );
                }
            }
        },
    });

    ExperimentUtils.setUserAttributes({
        landingPageType:
            pageType === 'destination' && destination.has_upcoming_events
                ? 'venue'
                : pageType,
    });

    const experiments = ExperimentUtils.getBuckets();

    return {
        optimizelyDatafile,
        experiments,
    };
};

export default startup;
